/**
 *
 * There are various permutations for how we can initiate a new conversation (see: ConversationType & ConversationRouterState)
 * There are various surfaces that can trigger a conversation; some initiate a conversation with the same parameteres (see CtaSource)
 *
 * To create a new entry point:
 * 1. Add a new enum CtaSource that represents the new triggering surface
 * 2. See if there is a ConversationRouterState that matches data you'd need to seed
 * 3. Update the switch/case statement & overload args in buildConversationRouterState
 */
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import type {
  FSSFile,
  FSSFolder,
} from "@mirage/service-fss-bridge/service/bridge_types";
import type { AgentId, WorkflowAgentRouteState } from "./types";

/** CtaSource represent the various surfaces that will call into the assistant page
 */
export enum CtaSource {
  OneClickAskPrompt = "one_click_ask_prompt",
  // This will come as M1
  // OneClickWritePrompt = 'one_click_write_prompt',
  OmniboxAskQuery = "omnibox_ask_query",
  // ask suggestions with sources that are used for generating suggestions
  OmniboxAskFromSuggestion = "omnibox_ask_from_suggestion",
  OmniboxDraftWithTemplate = "omnibox_draft_with_template",
  OmniboxDraftWithTone = "omnibox_draft_with_tone",
  OmniboxDraftWithDocument = "omnibox_draft_with_document",
  OmniboxDraftWithMessage = "omnibox_draft_with_message",
  OmniboxRecentChat = "omnibox_recent_chat",
  // SERP
  SerpSummarize = "serp_summarize",
  // Stack
  StackChat = "stack_chat",
  StackSuggestedPrompt = "stack_suggested_prompt",
  StackSummarize = "stack_summarize",
  StackItemSummarize = "stack_item_summarize",
  // Start page
  FeedItemSummarize = "feed_item_summarize",
  RecentSummarize = "recent_summarize",
  StartPageStackChat = "start_page_stack_chat",
  // Workflow Agent
  WorkflowAgent = "workflow_agent",
  // Legacy
  LegacySession = "legacy_session",
  // Conversations Page
  DraftWithTemplate = "draft_with_template",
  // FSS (embedded chat)
  FssUnscoped = "fss_unscoped",
  FssAskFromSuggestion = "fss_ask_from_suggestion",
  FssAskQuery = "fss_ask_query",
  FssAskWithFile = "fss_ask_with_file",
  FssFileSummarize = "fss_file_summarize",
  FssAskWithMultiFile = "fss_ask_with_multi_file",
  FssAskWithFolder = "fss_ask_with_folder",
  FssFolderSummarize = "fss_folder_summarize",
  // Deep Link
  DeepLinkWrite = "deep_link_write",
  DeepLinkWriteWithTemplate = "deep_link_write_with_template",
  // Document Preview Modal
  DocumentPreviewSuggestion = "document_preview_suggestion",
}

// ====================== ConversationTypes and isXFn() utilities ======================
// ConversationType are all the permutations that we'd seed a conversation when we navigate to the assistant
// these are utility types to ensure we have type safety when reading data from router state
export enum ConversationType {
  Ask = "Ask",
  AskFromSuggestion = "AskFromSuggestion",
  WriteFromTemplate = "WriteFromTemplate",
  WriteFromTone = "WriteFromTone",
  WriteWithDocument = "WriteWithDocument",
  WriteWithMessage = "WriteWithMessage",
  AskWithFollowup = "AskWithFollowup",
  AskWithMultiDocs = "AskWithMultiDocs",
  AskWithPromptMultiDocs = "AskWithPromptMultiDocs",
  SummarizeItem = "SummarizeItem",
  WorkflowAgent = "WorkflowAgent",
  LegacySession = "LegacySession",
  RecentChat = "RecentChat",
  // FSS (embedded chat)
  FssAsk = "FssAsk",
  FssAskQuery = "FssAskQuery",
  FssAskFromSuggestion = "FssAskFromSuggestion",
  FssAskWithMultiDocs = "FssAskWithMultiDocs",
  FssAskWithFolder = "FssAskWithFolder",
}

export type ConversationRouterState =
  | AskConversationRouterState
  | AskFromSuggestionConversationRouterState
  | WriteFromTemplateRouterState
  | WriteFromToneRouterState
  | WriteWithDocumentRouterState
  | WriteWithMessageRouterState
  | AskWithFollowupRouterState
  | SummarizeItemRouterState
  | AskWithMultiDocsRouterState
  | AskWithPromptMultiDocsRouterState
  | WorkflowAgentRouterState
  | LegacySessionRouterState
  | RecentChatRouterState
  | FssAskConversationRouterState
  | FssAskQueryConversationRouterState
  | FssAskFromSuggestionConversationRouterState
  | FssAskWithMultiDocsRouterState;

// Set of dynamic params we can specify for creating a new conversation router state
// Intentionally not including skipSearch as this is better set by builder function
// in ensure a single source of truth
export type DynamicParams = {
  // don't include skipSearch
  message: string;
  templateId: string;
  voiceId: string;
  attachments: string[];
  agentIds: AgentId[];
  chatSources: ChatSource[];
  conversationId: string; // passing this up front means we already have a converation; one was created
  scenarioMeta: ScenarioMeta;
  serverConversationId: string;
  messageHistory?: MessageHistory[]; // Added for legacy sessions
  markdownContent?: string; // Added for legacy sessions markdown draft
};
export type ScenarioMeta =
  | {
      type: "stack";
      sharingId: string;
    }
  | {
      type: "stack_item_summarize";
      stackSharingId: string;
    }
  | {
      type: "search";
      query?: string;
      searchSessionId: string | undefined;
      searchQueryUuid: string | undefined;
    }
  | { type: "feed" }
  | {
      type: "omnibox_summarize_shortcut";
      query?: string;
      searchSessionId: string | undefined;
      // NOTE: we don't pass in searchQueryUuid here because searchQueryUuid
      // is more of a SERP concept
    }
  | {
      type: "workflow_agent";
      workflowAgentId: string;
      model: string;
    }
  | {
      type: "fss_serp_query";
      query: string;
    }
  | {
      type: "fss_file";
      files: FSSFile[];
    }
  | {
      type: "fss_folder";
      folder: FSSFolder;
    }
  | { type: "other" };

interface BaseRouterState {
  ctaSource: CtaSource;
  conversationType: ConversationType;
  scenarioMeta?: ScenarioMeta;
}

// ========== Conversation Type Specific Router States ========
/** AskConversationRouterState
 *  used in various surfaces such as the one-click ask prompt, omnibox ask query, etc.
 *
 */
export interface AskConversationRouterState extends BaseRouterState {
  conversationType: ConversationType.Ask;
  message: string;
}

export type AskParams = Pick<DynamicParams, "message">;

export function isAskConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is AskConversationRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.Ask,
  );
}

export interface AskFromSuggestionConversationRouterState
  extends BaseRouterState {
  conversationType: ConversationType.AskFromSuggestion;
  message: string;
  attachments: string[]; // NOTE: these are the sources used for generating suggestions
  agentIds: AgentId[];
}

export type AskFromSuggestionParams = Pick<
  DynamicParams,
  "message" | "attachments" | "agentIds"
>;

export function isAskFromSuggestionConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is AskFromSuggestionConversationRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.AskFromSuggestion,
  );
}

/** AskWithMultiDocsRouterState
 *  used in the stack chat surface, supports future areas where we might want to pass in multiple uuids
 *
 */
export interface AskWithMultiDocsRouterState extends BaseRouterState {
  chatSources: ChatSource[];
  conversationType: ConversationType.AskWithMultiDocs;
  scenarioMeta: ScenarioMeta;
}

// we need to export the type to ensure we satisify args when building router state
export type AskWithMultiDocsParams = Pick<
  DynamicParams,
  "chatSources" | "scenarioMeta"
>;

export function isAskWithMultiDocsConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is AskWithMultiDocsRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.AskWithMultiDocs,
  );
}

/** AskWithPromptMultiDocsRouterState
 *  Used when coming from the Stack surface via a suggested query; Supports passing a message alongside a collection of uuid sources.
 *
 */
export interface AskWithPromptMultiDocsRouterState extends BaseRouterState {
  chatSources: ChatSource[];
  conversationType: ConversationType.AskWithPromptMultiDocs;
  message: string;
  scenarioMeta: ScenarioMeta;
}

export type AskWithPromptMultiDocsParams = Pick<
  DynamicParams,
  "chatSources" | "message" | "scenarioMeta"
>;

export function isAskWithPromptMultiDocsConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is AskWithPromptMultiDocsRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.AskWithPromptMultiDocs,
  );
}

/** WriteWithMessageRouterState
 *  used in the omnibox draft with message surface
 *
 */
export interface WriteWithMessageRouterState extends BaseRouterState {
  conversationType: ConversationType.WriteWithMessage;
  message: string;
  agentIds: AgentId[];
}

export type WriteWithMessageParams = Pick<DynamicParams, "message">;

export function isWriteWithMessageConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is WriteWithMessageRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.WriteWithMessage,
  );
}

/** WriteFromTemplateRouterState
 *  used in the omnibox draft with template surface
 *
 */
export interface WriteFromTemplateRouterState extends BaseRouterState {
  conversationType: ConversationType.WriteFromTemplate;
  templateId: string;
  agentIds: AgentId[];
}

export type WriteFromTemplateParams = Pick<DynamicParams, "templateId">;

export type WriteFromDeepLinkParams = Pick<DynamicParams, "message">;

export function isWriteFromTemplateConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is WriteFromTemplateRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.WriteFromTemplate,
  );
}

/**
 * WriteFromToneRouterState
 *  used in the omnibox draft with tone surface
 *
 */
export interface WriteFromToneRouterState extends BaseRouterState {
  conversationType: ConversationType.WriteFromTone;
  voiceId: string;
  agentIds: AgentId[];
}

export type WriteFromToneParams = Pick<DynamicParams, "voiceId">;

export function isWriteFromToneConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is WriteFromToneRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.WriteFromTone,
  );
}

/** WriteWithDocumentRouterState
 *  used in the omnibox draft with document surface, but could be used on other
 *  surfaces like feed etc.
 *
 */
export interface WriteWithDocumentRouterState extends BaseRouterState {
  conversationType: ConversationType.WriteWithDocument;
  chatSources: ChatSource[];
  agentIds: AgentId[];
}

export type WriteWithDocumentParams = Pick<DynamicParams, "chatSources">;

export function isWriteWithDocumentConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is WriteWithDocumentRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.WriteWithDocument,
  );
}

/** SummarizeItemRouterState
 *  used in the serp summarize surface, but also for stack_item summarization and feed items
 *
 */
export interface SummarizeItemRouterState extends BaseRouterState {
  conversationType: ConversationType.SummarizeItem;
  chatSources: ChatSource[];
  scenarioMeta: ScenarioMeta;
}

export type SummarizeItemParams = Pick<
  DynamicParams,
  "chatSources" | "scenarioMeta"
>;

export function isSummarizeItemConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is SummarizeItemRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.SummarizeItem,
  );
}

/** AskWithFollowupRouterState
 *  used in the serp answer follow up surface (currently not integrated with follow-up)
 *
 */
export interface AskWithFollowupRouterState extends BaseRouterState {
  conversationType: ConversationType.AskWithFollowup;
  // NOTE: Until persistence comes in, we may need to send cached message history
  // history: messages,
  conversationId: string;
  message: string;
  scenarioMeta: ScenarioMeta;
}

export type AskWithFollowupParams = Pick<
  DynamicParams,
  "conversationId" | "message" | "scenarioMeta"
>;

export function isAskWithFollowupConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is AskWithFollowupRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.AskWithFollowup,
  );
}

/** WorkflowAgentRouterState
 *  used in the workflow agent surface
 *
 */
export interface WorkflowAgentRouterState extends BaseRouterState {
  conversationType: ConversationType.WorkflowAgent;
  scenarioMeta: ScenarioMeta;
}

export type WorkflowAgentParams = Pick<DynamicParams, "scenarioMeta">;

export function isWorkflowAgentConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is WorkflowAgentRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.WorkflowAgent,
  );
}

// small util function to ensure we have the necessary properties in the params
export const requiredPropertiesExist = <K extends keyof DynamicParams>(
  params: Partial<DynamicParams>,
  properties: Array<K>,
): params is Pick<DynamicParams, K> => {
  for (const property of properties) {
    if (params[property] === undefined) {
      return false;
    }
  }
  return true;
};

// Define a message history interface for legacy sessions
export interface MessageHistory {
  role: "user" | "assistant";
  content: string;
  attachments?: ChatSource[]; // Using ChatSource instead of any
}

/** LegacySessionRouterState
 *  Used when importing a conversation from a legacy session
 */
export interface LegacySessionRouterState extends BaseRouterState {
  conversationType: ConversationType.LegacySession;
  messageHistory: MessageHistory[];
  markdownContent?: string; // Optional markdown content from legacy session
}

export type LegacySessionParams = Pick<
  DynamicParams,
  "messageHistory" | "markdownContent"
>;

export function isLegacySessionConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is LegacySessionRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.LegacySession,
  );
}

/**
 *  RecentChatRouterState
 *  Used when navigating to a recent chat from the omnibox or other surfaces
 */
export interface RecentChatRouterState extends BaseRouterState {
  conversationType: ConversationType.RecentChat;
}

// RecentChat currently does not support any params
export type RecentChatParams = Record<string, never>;

export function isRecentChatConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is RecentChatRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.RecentChat,
  );
}

// ========== FSS Conversation Type Specific Router States ========
/** FssAskConversationRouterState
 *  used for FSS unscoped ask
 */
export interface FssAskConversationRouterState extends BaseRouterState {
  conversationType: ConversationType.FssAsk;
}

export type FssAskParams = Record<string, never>;

export function isFssAskConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is FssAskConversationRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.FssAsk,
  );
}

/** FssAskQueryConversationRouterState
 *  used for FSS ask query
 */
export interface FssAskQueryConversationRouterState extends BaseRouterState {
  conversationType: ConversationType.FssAskQuery;
  message: string;
  scenarioMeta: ScenarioMeta;
}

export type FssAskQueryParams = Pick<DynamicParams, "message" | "scenarioMeta">;

export function isFssAskQueryConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is FssAskQueryConversationRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.FssAskQuery,
  );
}

/** FssAskFromSuggestionConversationRouterState
 *  used for FSS ask from suggestion
 */
export interface FssAskFromSuggestionConversationRouterState
  extends BaseRouterState {
  conversationType: ConversationType.FssAskFromSuggestion;
  message: string;
  chatSources: ChatSource[];
  agentIds: AgentId[];
  scenarioMeta: ScenarioMeta;
}

export type FssAskFromSuggestionParams = Pick<
  DynamicParams,
  "message" | "chatSources" | "agentIds" | "scenarioMeta"
>;

export function isFssAskFromSuggestionConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is FssAskFromSuggestionConversationRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.FssAskFromSuggestion,
  );
}

/** FssAskWithMultiDocsRouterState
 *  used for FSS ask with file and FSS ask with multi file
 */
export interface FssAskWithMultiDocsRouterState extends BaseRouterState {
  conversationType: ConversationType.FssAskWithMultiDocs;
  chatSources: ChatSource[];
  scenarioMeta: ScenarioMeta;
}

export type FssAskWithMultiDocsParams = Pick<
  DynamicParams,
  "chatSources" | "scenarioMeta"
>;

export function isFssAskWithMultiDocsConversationState(
  state: ConversationRouterState | undefined | WorkflowAgentRouteState,
): state is FssAskWithMultiDocsRouterState {
  return Boolean(
    state &&
      "ctaSource" in state &&
      state.conversationType === ConversationType.FssAskWithMultiDocs,
  );
}
